import { useEffect } from 'react'
import { Button, Text, VStack } from '@chakra-ui/react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { grantApi } from '../../lib/api'
import { writeGrantMirror } from '../../lib/grantMirror'
import { wipeOfflineData } from '../../lib/offlineStore'
import { getReturnCredential, clearReturnCredential } from '../../lib/returnCredential'
import { storeGrantSessionToken } from '../../lib/session/grantSessionToken'
import { dropRejectedGrant } from '../../lib/native/wipeAckCredential'
import { WelcomeBackCard } from '../../components/enroll/WelcomeBackCard'

/**
 * Landing screen for a signed-out canvasser device: session expired/revoked,
 * or no grant was ever redeemed on this device. Real re-entry is always via
 * a fresh invite link (/canvasser/redeem?token=...) — this page has no form.
 * In dev builds only, a manual dev-session affordance skips needing a real
 * invite link while iterating locally.
 */
export function CanvasserSignedOutPage() {
  const navigate = useNavigate()
  const devEnabled = import.meta.env.DEV

  // [LEGAL] The revoke/expiry path lands here; wipe cached PII on mount (spec §3.1).
  // Idempotent — safe when nothing is cached, and complements logout's own wipe. On this
  // server-driven path the session cookie survives the revoke (web), so confirm the wipe to the
  // server (WipeConfirmedAt) once the wipe succeeds — best-effort, ignore the result. On native a
  // data 401 already ran this wipe + ack itself before its reloading navigation (api.ts, N1); this
  // ack only carries the retained gs_ (wipeAckCredential.ts) when the page was reached client-side
  // (RequireGrant after a /grant/me 401). The retained native value is dropped once it settles.
  useEffect(() => {
    void wipeOfflineData()
      .then(() => grantApi.wipeAck().then(() => {}, () => {}))
      .catch(() => {})
      .finally(dropRejectedGrant)
  }, [])

  const returnCred = getReturnCredential()
  if (returnCred)
    return (
      <WelcomeBackCard
        token={returnCred.token}
        onUseDifferentCode={() => { clearReturnCredential(); navigate('/join', { replace: true }) }}
      />
    )

  const devSignIn = async () => {
    const grantId = window.prompt('Dev: seeded grant id?') ?? ''
    if (!grantId) return
    try {
      const { data } = await grantApi.devSession({ grantId })
      await storeGrantSessionToken(data) // [Native B1] the gs_ rides the body on native; no-op on web
      writeGrantMirror({
        grantId: data.grantId,
        kind: data.kind,
        subjectContactId: data.subjectContactId,
        ownerCommitteeId: data.ownerCommitteeId,
      })
      navigate('/canvasser', { replace: true })
    } catch {
      // 404 when the dev-session flag is off, or an invalid seeded id; ignore.
    }
  }

  return (
    <VStack py={12} px={6} gap={4}>
      <Text color="fg.muted" textAlign="center">
        Your session has ended. Open your invite link again to sign back in.
      </Text>
      {/* Escape hatch off this dead-end: the front-door chooser ("Join as a canvasser" /
          "Organizer sign in") so a signed-out device isn't stranded without re-typing the URL. */}
      <Button asChild colorPalette="brand" minH="44px">
        <RouterLink to="/">Back to start</RouterLink>
      </Button>
      {devEnabled && (
        <Button variant="outline" minH="44px" onClick={devSignIn}>
          Dev sign-in
        </Button>
      )}
    </VStack>
  )
}
